import { IconNext } from '@assets/icon-next';
import { IconPrev } from '@assets/icon-prev';
import type { Collection } from '@lib/schema';
import type { Genre, Provider } from '@lib/streaming';
import { IconStar } from '@src/assets/icon-star';
import type { TvChannel } from '@src/lib/graphql/generated';
import { Image, type PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export interface EntryCarouselProps extends PropsWithClassProps<'entry' | 'entryContainer' | 'carouselRoot'> {
  entries: Genre[] | Provider[] | TvChannel[] | Collection[];
  onEntryChange?: (Item) => void;
  type?: 'text' | 'title' | 'image';
  primaryProperty: string;
  altProperty?: string;
  target?: string;
  isTvChannels?: boolean;
  hideHeading?: boolean;
}

export const EntryCarousel = ({
  entries,
  type = 'text',
  target,
  primaryProperty,
  altProperty,
  onEntryChange,
  isTvChannels,
  hideHeading,
  classProps,
}: EntryCarouselProps) => {
  const [activeEntryIndex, setActiveEntryIndex] = useState(0);
  const [directMove, setDirectMove] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const entryItems = [].concat(entries);

  const originalLength = entryItems.length;
  if (originalLength > 0 && originalLength < 5) {
    const addCount = 5 - originalLength + (originalLength % 2 === 0 ? 1 : 0);
    for (let i = 0; i < addCount; i++) {
      entryItems.push(entryItems[i % originalLength]);
    }
  }

  const changeActiveIndex = (Count) => {
    const len = entryItems.length;
    if (len === 0) return;
    const newTarget =
      activeEntryIndex + Count >= len
        ? (activeEntryIndex + Count) % len
        : activeEntryIndex + Count < 0
          ? activeEntryIndex + Count + len
          : activeEntryIndex + Count;
    setActiveEntryIndex(newTarget);
    onEntryChange(entryItems[newTarget]);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: entryItems is recreated every render, deps intentionally limited
  useEffect(() => {
    setLoaded(true);
    if (!target) return;
    const targetIndex = entryItems.findIndex((e) => e?.id === target);

    const moveCount = targetIndex - activeEntryIndex;
    setDirectMove(true);
    changeActiveIndex(moveCount);
    setTimeout(() => {
      setDirectMove(false);
    }, 200);
  }, [target]);

  if (!entryItems || entryItems.length < 1) return;

  const outerLeftIndex =
    activeEntryIndex - 2 < -1
      ? entryItems.length - 2
      : activeEntryIndex - 2 < 0
        ? entryItems.length - 1
        : activeEntryIndex - 2;
  const leftIndex = activeEntryIndex - 1 < 0 ? entryItems.length - 1 : activeEntryIndex - 1;
  const rightIndex = (activeEntryIndex + 1) % entryItems.length;
  const outerRightIndex = (activeEntryIndex + 2) % entryItems.length;

  const styles = {
    base: twMerge(
      'absolute inset-0 rounded-lg origin-center text-white font-herokid text-center grid place-items-center hyphenate ',
      directMove ? 'transition-none' : 'transition duration-300',
    ),
    outerLeft: 'translate-x-[-100%] md:translate-x-[-150%] scale-25 opacity-0',
    left: 'translate-x-[-75%] md:translate-x-[-100%] scale-50 opacity-50',
    center: 'z-10',
    right: 'translate-x-[75%] md:translate-x-[100%] scale-50 opacity-50',
    outerRight: 'translate-x-[100%] md:translate-x-[150%] scale-25 opacity-0',
  };

  return (
    <div
      className={twMerge(
        'group relative grid min-h-0 w-full items-center gap-4 overflow-hidden sm:grid-cols-[auto_1fr_auto]',
        classProps?.root,
      )}
    >
      <div className="absolute inset-y-0 left-6 z-30 grid items-center pointer:opacity-50 transition-opacity duration-150 group-hover:opacity-100 sm:static">
        <button
          type="button"
          aria-label="slide backwards"
          className="grid size-16 cursor-pointer place-items-center rounded-full border border-gray-650/65 bg-gray-820/70 text-gray-650/65 transition-all duration-300 hover:border-gray-400/75 hover:text-gray-400/75"
          onClick={() => {
            changeActiveIndex(-1);
          }}
        >
          <IconPrev classProps={{ root: 'w-7 h-4' }} />
        </button>
      </div>
      {!hideHeading && <h1 className="sr-only">{entryItems[activeEntryIndex]?.[altProperty || primaryProperty]}</h1>}
      <div className="block size-full">
        <div
          className={twMerge(
            'grid h-full w-full items-center justify-center',
            type === 'image' && 'aspect-video md:aspect-auto',
          )}
        >
          <div
            className={twMerge(
              type === 'image'
                ? 'relative aspect-video h-full min-h-full w-full min-w-full opacity-0 transition-opacity duration-200 md:h-[40vh]'
                : 'relative aspect-[3/4] h-full min-h-full w-full min-w-full opacity-0 transition-opacity duration-200 sm:aspect-square md:h-[40vh]',
              loaded && 'opacity-100',
              classProps?.carouselRoot,
            )}
          >
            {entryItems?.map((entry, index) => (
              <div
                className={twMerge(
                  styles.base,
                  index === outerLeftIndex
                    ? styles.outerLeft
                    : index === leftIndex
                      ? styles.left
                      : index === activeEntryIndex
                        ? styles.center
                        : index === rightIndex
                          ? styles.right
                          : index === outerRightIndex
                            ? styles.outerRight
                            : 'opacity-0',
                )}
                key={entry?.id + index}
              >
                <div className={classProps?.entryContainer}>
                  {isTvChannels && (
                    <div
                      className={twMerge(
                        'absolute top-0 h-2 w-full bg-gray-500 transition duration-100',
                        entry?.favorite?.id ? 'bg-primary text-primary' : 'text-gray-500/50 hover:text-primary',
                      )}
                    >
                      <div className="absolute top-0 right-0 mt-3 cursor-pointer p-4">
                        <IconStar classProps={{ root: 'size-6 stroke-2' }} filled={!!entry?.favorite?.id} />
                      </div>
                    </div>
                  )}
                  {type === 'title' ? (
                    <span className={classProps?.entry}>{entry?.[primaryProperty]}</span>
                  ) : type === 'text' ? (
                    <span className={classProps?.entry}>{entry?.[primaryProperty]}</span>
                  ) : (
                    <div className={classProps?.entry}>
                      {entry?.[primaryProperty] ? (
                        <Image
                          src={entry?.[primaryProperty]}
                          alt={entry?.[altProperty]}
                          fill
                          sizes="300px"
                          copyright=""
                        />
                      ) : (
                        <span>{entry?.[altProperty]}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute inset-y-0 right-6 z-30 grid items-center pointer:opacity-20 transition-opacity duration-150 group-hover:opacity-100 sm:static">
        <button
          type="button"
          aria-label="slide forward"
          className="grid size-16 cursor-pointer place-items-center rounded-full border border-gray-650/65 bg-gray-820/70 text-gray-650/65 transition-all duration-300 hover:border-gray-400/75 hover:text-gray-400/75"
          onClick={() => {
            changeActiveIndex(1);
          }}
        >
          <IconNext classProps={{ root: 'w-7 h-4' }} />
        </button>
      </div>
      <div className="absolute inset-y-0 -left-16 w-1/3 bg-linear-to-r from-black via-black/80 to-black/0"></div>
      <div className="absolute inset-y-0 -right-16 w-1/3 bg-linear-to-l from-black via-black/80 to-black/0"></div>
      <div className="radial-carousel-switcher-background absolute inset-x-[10%] inset-y-0"></div>
    </div>
  );
};
